import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Ticket, Sparkles, CreditCard } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { useCreatePunchCardWithInvoice } from '@/hooks/usePatientPunchCards';
import {
  useActivePunchCardTemplates,
  useCreatePunchCardFromTemplate,
} from '@/hooks/usePunchCardTemplates';
import { usePurchasePunchCard } from '@/hooks/usePortalPunchCards';
import { useClinic } from '@/hooks/useClinicSettings';
import { VatMomsField } from '@/components/ui/VatMomsField';
import { clinicVatRatePercent, itemIncludesVat, itemVatRatePercent } from '@/lib/vat';

interface CreatePunchCardModalProps {
  patientId: string;
  clinicId: string;
  onClose: () => void;
}

export function CreatePunchCardModal({ patientId, clinicId, onClose }: CreatePunchCardModalProps) {
  const { t } = useTranslation();
  const createPunchCard = useCreatePunchCardWithInvoice();
  const createFromTemplate = useCreatePunchCardFromTemplate();
  const purchaseOnline = usePurchasePunchCard();
  const { data: templates = [] } = useActivePunchCardTemplates(clinicId);
  const { data: clinic } = useClinic(clinicId);
  const clinicVatRate = clinicVatRatePercent(clinic);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [name, setName] = useState('');
  const [totalClips, setTotalClips] = useState(10);
  const [price, setPrice] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [createInvoice, setCreateInvoice] = useState(true);
  const [includesVat, setIncludesVat] = useState(true);

  // When template is selected, pre-fill the form
  useEffect(() => {
    if (selectedTemplateId) {
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (template) {
        setName(template.name);
        setTotalClips(template.total_clips);
        setPrice((template.price_cents / 100).toFixed(2));
        // Calculate expiry if template has expires_months
        if (template.expires_months) {
          const expiryDate = new Date();
          expiryDate.setMonth(expiryDate.getMonth() + template.expires_months);
          setExpiresAt(expiryDate.toISOString().split('T')[0]);
        } else {
          setExpiresAt('');
        }
        setIncludesVat(itemIncludesVat(template.vat_rate_percent));
      }
    } else {
      setIncludesVat(true);
    }
  }, [selectedTemplateId, templates]);

  const handleSubmit = async () => {
    // If template is selected, use the quick template creation
    if (selectedTemplateId) {
      try {
        await createFromTemplate.mutateAsync({
          template_id: selectedTemplateId,
          patient_id: patientId,
          create_invoice: createInvoice,
        });

        toast.success(t('patients.punch_cards.created_success', 'Punch card created successfully'));
        onClose();
        return;
      } catch (error) {
        toast.error(t('patients.punch_cards.create_error', 'Failed to create punch card'));
        return;
      }
    }

    // Otherwise, use manual creation
    if (!name.trim()) {
      toast.error(t('patients.punch_cards.name_required', 'Please enter a name'));
      return;
    }

    if (totalClips < 1 || totalClips > 100) {
      toast.error(
        t('patients.punch_cards.clips_range_error', 'Total clips must be between 1 and 100'),
      );
      return;
    }

    const priceValue = parseFloat(price.replace(',', '.'));
    if (isNaN(priceValue) || priceValue < 0) {
      toast.error(t('patients.punch_cards.price_required', 'Please enter a valid price'));
      return;
    }

    const priceCents = Math.round(priceValue * 100);

    try {
      await createPunchCard.mutateAsync({
        clinic_id: clinicId,
        patient_id: patientId,
        name: name.trim(),
        total_clips: totalClips,
        price_cents: priceCents,
        currency: 'DKK',
        expires_at: expiresAt || null,
        appointment_types: [],
        group_types: [],
        create_invoice: createInvoice,
        vat_rate_percent: itemVatRatePercent(includesVat, clinicVatRate),
      });

      toast.success(t('patients.punch_cards.created_success', 'Punch card created successfully'));
      onClose();
    } catch (error) {
      toast.error(t('patients.punch_cards.create_error', 'Failed to create punch card'));
    }
  };

  const selectedTemplate = templates.find((tpl) => tpl.id === selectedTemplateId);
  const canPayOnline = Boolean(selectedTemplate && selectedTemplate.price_cents > 0);
  const isBusy =
    createPunchCard.isPending || createFromTemplate.isPending || purchaseOnline.isPending;

  const handlePayOnline = async () => {
    if (!selectedTemplateId) {
      toast.error(
        t('patients.punch_cards.select_template_for_pay', 'Select a template to pay online'),
      );
      return;
    }
    try {
      const result = await purchaseOnline.mutateAsync({
        templateId: selectedTemplateId,
        patientId,
        returnPath: `/patients/${patientId}`,
      });
      window.location.href = result.checkout_url;
    } catch (error) {
      toast.error(t('patients.punch_cards.pay_online_error', 'Could not start online payment'));
    }
  };

  return (
    <SlideOver
      open
      title={t('patients.punch_cards.new_card')}
      onClose={onClose}
      dirty={Boolean(name || price || selectedTemplateId)}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          primary={
            <div className="flex flex-wrap items-center gap-s2">
              {canPayOnline ? (
                <Button
                  variant="secondary"
                  onClick={() => void handlePayOnline()}
                  disabled={isBusy}
                  loading={purchaseOnline.isPending}
                >
                  <CreditCard className="h-4 w-4" />
                  {t('patients.punch_cards.pay_online')}
                </Button>
              ) : null}
              <Button
                onClick={() => void handleSubmit()}
                disabled={isBusy || !name.trim() || !price}
                loading={createPunchCard.isPending || createFromTemplate.isPending}
              >
                {t('patients.punch_cards.create')}
              </Button>
            </div>
          }
        />
      }
    >
      <div className="space-y-s4">
        {templates.length > 0 ? (
          <div className="rounded-pop border border-ds-teal-line bg-ds-teal-tint p-s4">
            <div className="mb-s3 flex items-center gap-s2">
              <Sparkles className="h-4 w-4 text-ds-teal" />
              <p className={cn(TYPE.label, 'text-ds-text')}>{t('patients.punch_cards.use_template')}</p>
            </div>
            <Select
              id="punch-card-template"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
            >
              <option value="">{t('patients.punch_cards.select_template')}</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} — {template.total_clips} {t('patients.punch_cards.clips')} —{' '}
                  {(template.price_cents / 100).toFixed(0)} kr
                </option>
              ))}
            </Select>
          </div>
        ) : null}

        <div className="flex items-center gap-s3 rounded-pop bg-ds-sunk p-s4">
          <span className="flex h-10 w-10 items-center justify-center rounded-ctl bg-ds-teal-tint text-ds-teal">
            <Ticket className="h-5 w-5" />
          </span>
          <div>
            <p className={cn(TYPE.section, 'text-ds-text')}>
              {selectedTemplateId
                ? t('patients.punch_cards.template_selected')
                : t('patients.punch_cards.create_title')}
            </p>
            <p className={cn(TYPE.small, 'text-ds-muted')}>
              {t('patients.punch_cards.create_description')}
            </p>
          </div>
        </div>

        <Input
          id="punch-card-name"
          required
          label={t('patients.punch_cards.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('patients.punch_cards.name_placeholder')}
        />
        <Input
          id="punch-card-clips"
          type="number"
          required
          min={1}
          max={100}
          label={t('patients.punch_cards.total_clips')}
          value={totalClips}
          onChange={(e) =>
            setTotalClips(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))
          }
        />
        <Input
          id="punch-card-price"
          inputMode="decimal"
          required
          label={t('patients.punch_cards.price_dkk')}
          value={price}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '' || /^\d*[.,]?\d{0,2}$/.test(raw)) setPrice(raw);
          }}
          placeholder="500.00"
        />
        <VatMomsField
          includesVat={includesVat}
          clinicRatePercent={clinicVatRate}
          onChange={setIncludesVat}
          disabled={Boolean(selectedTemplateId)}
        />
        <Input
          id="punch-card-expires"
          type="date"
          label={t('patients.punch_cards.expires_at')}
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          min={new Date().toISOString().split('T')[0]}
        />
        <Checkbox
          id="create-invoice"
          checked={createInvoice}
          onChange={(e) => setCreateInvoice(e.target.checked)}
          label={t('patients.punch_cards.create_invoice_label')}
        />
      </div>
    </SlideOver>
  );
}
